import { findFeatureByPath, FEATURE_MAP } from '@/admin/featureRegistry';
import { recordRequest } from '@/admin/requestMonitor';

export const API_BASE = (
  import.meta.env.VITE_API_BASE !== undefined && import.meta.env.VITE_API_BASE !== ''
    ? import.meta.env.VITE_API_BASE
    : (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
        ? ''
        : '')
).replace(/\/$/, '');

function getSessionUserId() {
  try {
    const raw = localStorage.getItem('school_session');
    if (!raw) return null;
    const session = JSON.parse(raw);
    return session?.id ? String(session.id) : null;
  } catch (err) {
    return null;
  }
}

function getAdminConfig() {
  try {
    const raw = localStorage.getItem('te_admin_v2') || localStorage.getItem('te_admin_v1');
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export async function request(path, options = {}) {
  let url = path;
  if (!path.startsWith('http')) {
    const clean = path.startsWith('/') ? path : `/${path}`;
    const base = API_BASE.replace(/\/api\/?$/, '');
    const apiPath = clean.startsWith('/api/') || clean === '/api' ? clean : `/api${clean}`;
    url = `${base}${apiPath}`;
  }

  const method = (options.method || 'GET').toUpperCase();
  const cleanPath = path.startsWith('http') ? new URL(path).pathname : path;
  const featureId = findFeatureByPath(cleanPath) || 'core';
  const adminConfig = getAdminConfig();

  // 1. Kill switch or offline active (exempt operator's /admin/bootstrap and /health)
  const isInfraRequest = cleanPath.includes('/admin/bootstrap') || cleanPath.endsWith('/health');
  if (!isInfraRequest && adminConfig && (adminConfig.killSwitch || adminConfig.offline)) {
    const err = new Error('API is switched off in the Admin console');
    err.status = 503;
    recordRequest({ method, path: cleanPath, status: 503, ms: 0, featureId, blocked: true, error: err.message });
    throw err;
  }

  // 2. Feature Read-Only Guard on mutation requests (POST/PUT/DELETE)
  if (method !== 'GET' && adminConfig) {
    const featState = adminConfig.features?.[featureId];
    const isReadOnly = adminConfig.readOnlyAll || featState?.access === 'readonly';
    if (isReadOnly) {
      const featMeta = FEATURE_MAP.get(featureId);
      const label = featMeta?.label || featureId;
      const err = new Error(`Read-only: ${label} modifications are blocked in the Admin console`);
      err.status = 403;
      recordRequest({ method, path: cleanPath, status: 403, ms: 0, featureId, blocked: true, error: err.message });
      throw err;
    }
  }

  // 3. Feature Disabled
  if (adminConfig && adminConfig.features?.[featureId]?.enabled === false) {
    const featMeta = FEATURE_MAP.get(featureId);
    const label = featMeta?.label || featureId;
    const err = new Error(`Feature '${label}' is disabled in the Admin console`);
    err.status = 403;
    recordRequest({ method, path: cleanPath, status: 403, ms: 0, featureId, blocked: true, error: err.message });
    throw err;
  }

  // 4. Simulated Latency
  if (adminConfig?.latencyMs > 0) {
    await new Promise((r) => setTimeout(r, adminConfig.latencyMs));
  }

  // 5. Simulated Fault Injection
  if (adminConfig?.failRate > 0 && Math.random() * 100 < adminConfig.failRate) {
    const err = new Error(`Simulated API failure (${adminConfig.failRate}% fault injection active)`);
    err.status = 500;
    recordRequest({ method, path: cleanPath, status: 500, ms: adminConfig.latencyMs || 10, featureId, error: err.message });
    throw err;
  }
  
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {})
  };

  const userId = getSessionUserId();
  if (userId && !headers['X-User-Id']) {
    headers['X-User-Id'] = userId;
  }

  const startTime = performance.now();
  let res;
  let json = null;

  try {
    res = await fetch(url, {
      ...options,
      headers
    });
    const elapsed = performance.now() - startTime;
    const rowsReadHeader = res.headers?.get('X-Rows-Read');
    const rowsRead = rowsReadHeader ? parseInt(rowsReadHeader, 10) : 0;
    const etag = res.headers?.get('ETag');

    // Handle 304 Not Modified cleanly without throwing
    if (res.status === 304) {
      recordRequest({ method, path: cleanPath, status: 304, ms: elapsed, rowsRead: 0, featureId });
      return { ok: true, status: 304, notModified: true, etag };
    }

    try {
      json = await res.json();
    } catch (e) {
      // Non-JSON response
    }

    if (!res.ok) {
      const errMsg = json?.error?.message || `HTTP ${res.status}: ${res.statusText}`;
      const err = new Error(errMsg);
      err.status = res.status;
      err.code = json?.error?.code;
      recordRequest({ method, path: cleanPath, status: res.status, ms: elapsed, rowsRead, featureId, error: errMsg });
      throw err;
    }

    if (json && json.ok === false) {
      const errMsg = json?.error?.message || 'API request failed';
      const err = new Error(errMsg);
      err.code = json?.error?.code;
      recordRequest({ method, path: cleanPath, status: 400, ms: elapsed, rowsRead, featureId, error: errMsg });
      throw err;
    }

    if (json && typeof json === 'object') {
      json._etag = etag;
    }

    recordRequest({ method, path: cleanPath, status: res.status, ms: elapsed, rowsRead, featureId });
    return json;
  } catch (err) {
    const elapsed = performance.now() - startTime;
    if (!err.status) {
      recordRequest({ method, path: cleanPath, status: 500, ms: elapsed, featureId, error: err.message });
    }
    throw err;
  }
}

export async function apiGet(path) {
  const res = await request(path, { method: 'GET' });
  return res?.data !== undefined ? res.data : res;
}

export async function apiGetWithMeta(path, options = {}) {
  const res = await request(path, { method: 'GET', ...options });
  if (res?.notModified) {
    return { notModified: true, status: 304, etag: res.etag };
  }
  return {
    data: res?.data !== undefined ? res.data : res,
    meta: res?.meta || null,
    etag: res?._etag || null
  };
}

export async function apiPost(path, body) {
  const res = await request(path, {
    method: 'POST',
    body: body !== undefined ? JSON.stringify(body) : undefined
  });
  return res?.data !== undefined ? res.data : res;
}

export async function apiPut(path, body) {
  const res = await request(path, {
    method: 'PUT',
    body: body !== undefined ? JSON.stringify(body) : undefined
  });
  return res?.data !== undefined ? res.data : res;
}

export async function apiDelete(path) {
  const res = await request(path, { method: 'DELETE' });
  return res?.data !== undefined ? res.data : res;
}

export const api = {
  get: apiGet,
  post: apiPost,
  put: apiPut,
  delete: apiDelete
};

/**
 * SWR fetcher that preserves envelope data and meta
 */
export async function fetcher(path) {
  const res = await request(path, { method: 'GET' });
  return res;
}
